/**
 * AdminBlogEditor — in-app blog composer at `/admin/blog`.
 *
 * Left rail: list of every post (draft, scheduled, published) with a
 *            "New post" button.
 * Right pane: markdown editor with a live-html preview split view.
 *
 * Uses the existing `mdToHtml` helper so we don't ship a heavy MD lib.
 * Publishing model:
 *   • `is_published=true`                → live immediately
 *   • `scheduled_publish_at` in future   → flipped live on first
 *     `/api/blog/posts` hit at/after that time (lazy sweep, no cron).
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import PageHero from '../../components/PageHero';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { mdToHtml } from '../../lib/richText';
import { format, parseISO } from 'date-fns';
import { Plus, Trash2, Save, Send, Clock, FileText, Loader2, Eye } from 'lucide-react';

const EMPTY = {
  id: null, slug: '', title: '', subtitle: '', body_markdown: '',
  hero_image_url: '', tags: '', category: '', is_published: false,
  scheduled_publish_at: '', excerpt: '',
};

const AdminBlogEditor = () => {
  const { API } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await axios.get(`${API}/admin/blog/posts`, { params: { limit: 100 } });
      setPosts(data?.posts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load posts');
    } finally { setLoadingList(false); }
  }, [API]);

  useEffect(() => { loadList(); }, [loadList]);

  const loadPost = async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/blog/posts/${id}`);
      setForm({
        ...EMPTY,
        ...data,
        tags: (data.tags || []).join(', '),
        scheduled_publish_at: data.scheduled_publish_at || '',
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load post');
    }
  };

  const savePost = async () => {
    if (!form.title || form.title.length < 3) {
      toast.error('Title is required (min 3 chars)');
      return;
    }
    if (!form.body_markdown || form.body_markdown.length < 10) {
      toast.error('Body is required (min 10 chars)');
      return;
    }
    setSaving(true);
    const payload = {
      title:                form.title,
      subtitle:             form.subtitle,
      body_markdown:        form.body_markdown,
      hero_image_url:       form.hero_image_url,
      tags:                 form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      category:             form.category,
      is_published:         !!form.is_published,
      slug:                 form.slug || undefined,
      excerpt:              form.excerpt || undefined,
      scheduled_publish_at: form.scheduled_publish_at || null,
    };
    try {
      let saved;
      if (form.id) {
        const { data } = await axios.put(`${API}/admin/blog/posts/${form.id}`, payload);
        saved = data;
      } else {
        const { data } = await axios.post(`${API}/admin/blog/posts`, payload);
        saved = data;
      }
      toast.success(saved.is_published ? 'Published' : 'Draft saved');
      await loadList();
      setForm({ ...EMPTY, ...saved, tags: (saved.tags || []).join(', '), scheduled_publish_at: saved.scheduled_publish_at || '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const deletePost = async () => {
    if (!form.id) return;
    if (!window.confirm('Delete this post permanently?')) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${form.id}`);
      toast.success('Deleted');
      setForm(EMPTY);
      await loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const previewHtml = useMemo(
    () => mdToHtml(form.body_markdown || ''),
    [form.body_markdown],
  );

  return (
    <div className="min-h-screen bg-background" data-testid="admin-blog-editor">
      <PageHero
        eyebrow="Admin"
        eyebrowIcon={FileText}
        title="Blog editor"
        description="Write, schedule, and publish long-form editorial. Scheduled posts auto-publish on their date."
        testId="admin-blog-hero"
      />
      <div className="container mx-auto max-w-7xl px-4 py-6 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Left: post list */}
        <aside className="space-y-2">
          <Button
            className="w-full rounded-full font-bold"
            onClick={() => setForm(EMPTY)}
            data-testid="admin-blog-new"
          >
            <Plus className="w-4 h-4 mr-1" /> New post
          </Button>
          <div className="space-y-1">
            {loadingList ? (
              <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading…
              </div>
            ) : posts.length === 0 ? (
              <p className="text-xs text-muted-foreground">No posts yet.</p>
            ) : posts.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => loadPost(p.id)}
                className={`w-full text-left rounded-lg border p-2 transition ${form.id === p.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}
                data-testid={`admin-blog-post-${p.id}`}
              >
                <div className="text-[10px] uppercase tracking-widest font-bold mb-0.5">
                  {p.is_published
                    ? <span className="text-emerald-600">Published</span>
                    : p.scheduled_publish_at
                      ? <span className="text-amber-600">Scheduled</span>
                      : <span className="text-muted-foreground">Draft</span>}
                </div>
                <div className="text-xs font-semibold line-clamp-2">{p.title}</div>
                {p.updated_at && (
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {format(parseISO(p.updated_at), 'MMM d · h:mm a')}
                  </div>
                )}
              </button>
            ))}
          </div>
        </aside>

        {/* Right: editor */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="How to freeze-dry berries perfectly" data-testid="admin-blog-title" />
              </div>
              <div>
                <Label>Slug (optional)</Label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="how-to-freeze-dry-berries" data-testid="admin-blog-slug" />
              </div>
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="A one-liner that shows under the title" data-testid="admin-blog-subtitle" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Hero image URL</Label>
                <Input value={form.hero_image_url} onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })} placeholder="https://…" data-testid="admin-blog-hero" />
              </div>
              <div>
                <Label>Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Freeze Drying Meals" data-testid="admin-blog-category" />
              </div>
            </div>
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="berries, produce, beginner" data-testid="admin-blog-tags" />
            </div>
            <div>
              <Label>Excerpt (auto-generated if blank)</Label>
              <Input value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Shown in list previews + meta description" data-testid="admin-blog-excerpt" />
            </div>

            <div className="flex items-center justify-between border-t border-b border-border py-2 my-2">
              <div className="flex items-center gap-3">
                <Switch checked={!!form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="admin-blog-publish-toggle" />
                <Label>Publish now</Label>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <Label htmlFor="admin-blog-schedule" className="text-xs">Schedule</Label>
                <Input
                  id="admin-blog-schedule"
                  type="datetime-local"
                  value={form.scheduled_publish_at ? form.scheduled_publish_at.slice(0, 16) : ''}
                  onChange={(e) => setForm({
                    ...form,
                    scheduled_publish_at: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : '',
                  })}
                  className="w-56"
                  data-testid="admin-blog-schedule-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Body (Markdown)</Label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowPreview((v) => !v)}
                  data-testid="admin-blog-preview-toggle"
                >
                  <Eye className="w-3.5 h-3.5 mr-1" /> {showPreview ? 'Hide preview' : 'Show preview'}
                </Button>
              </div>
              <div className={`grid gap-3 ${showPreview ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
                <Textarea
                  value={form.body_markdown}
                  onChange={(e) => setForm({ ...form, body_markdown: e.target.value })}
                  className="font-mono min-h-[420px]"
                  placeholder="## Introduction\n\nWrite your post in Markdown…"
                  data-testid="admin-blog-body"
                />
                {showPreview && (
                  <article
                    className="prose prose-slate dark:prose-invert max-w-none min-h-[420px] rounded-lg border border-border p-4 overflow-auto"
                    data-testid="admin-blog-preview"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                )}
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="text-[11px] text-muted-foreground">
                {form.id ? `Editing: ${form.title || 'untitled'}` : 'Creating new post'}
              </div>
              <div className="flex gap-2">
                {form.id && (
                  <Button
                    variant="outline"
                    onClick={deletePost}
                    className="rounded-full text-rose-600 border-rose-300 hover:bg-rose-50"
                    data-testid="admin-blog-delete"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                )}
                <Button
                  onClick={savePost}
                  disabled={saving}
                  className="rounded-full font-bold"
                  data-testid="admin-blog-save"
                >
                  {saving
                    ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</>
                    : form.is_published
                      ? <><Send className="w-4 h-4 mr-1" /> Publish</>
                      : <><Save className="w-4 h-4 mr-1" /> Save draft</>}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminBlogEditor;
